// services/omoreExportService.ts
// CSV export functionality for Omore analytics, moderation, sessions, and cohorts

import type {
    OmoreInteractionAggregatedAnalytics,
    FlaggedMessage,
    UserAbuseSummary,
    OmoreSessionDetail,
} from '../types/OmoreInteractionTypes';
import type { CohortAnalysis } from './omoreCohortAnalysisService';

// ─── CSV Field Escaper ──────────────────────────────────────

function escapeCsvField(field: string): string {
    if (!field) return '';
    if (field.includes(',') || field.includes('"') || field.includes('\n')) {
        return `"${field.replace(/"/g, '""')}"`;
    }
    return field;
}

// ─── Browser Download Helper ────────────────────────────────

export function downloadCSV(csvContent: string, filename: string): void {
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
}

// ─── Helper Type Labels ─────────────────────────────────────

const HELPER_TYPE_LABELS: Record<string, string> = {
    general: 'General Help',
    character: 'Character',
    setting: 'Setting',
    plot: 'Plot',
    dialogue: 'Dialogue',
    theme: 'Theme',
    worldBuilding: 'World Building',
    revision: 'Revision',
    brainstorm: 'Brainstorm',
};

const CATEGORY_LABELS: Record<string, string> = {
    self_harm: 'Self-Harm',
    violence: 'Violence',
    sexual: 'Sexual',
    harassment: 'Harassment',
    hate: 'Hate',
    inappropriate: 'Inappropriate',
    prompt_injection: 'Prompt Injection',
    other: 'Other',
};

// ─── Analytics Export ───────────────────────────────────────

export function exportAnalyticsToCSV(
    analytics: OmoreInteractionAggregatedAnalytics
): string {
    const rows: string[] = [];

    rows.push('Omore Analytics Export');
    rows.push(`Date Range,${analytics.startDate} to ${analytics.endDate}`);
    rows.push('');

    // Summary metrics section
    rows.push('SUMMARY METRICS');
    rows.push('Metric,Value');
    rows.push(`Total Sessions,${analytics.totalSessions}`);
    rows.push(`Unique Users,${analytics.uniqueUsers}`);
    rows.push(`Total Messages,${analytics.totalMessages}`);
    rows.push(`Suggestion Adoption Rate,${analytics.suggestionAdoptionRate.toFixed(2)}%`);
    rows.push(`Avg Session Duration (seconds),${analytics.avgSessionDurationSeconds.toFixed(2)}`);
    rows.push('');

    // Sessions by helper type
    rows.push('SESSIONS BY HELPER TYPE');
    rows.push('Helper Type,Session Count');
    Object.entries(analytics.sessionsByHelperType)
        .sort((a, b) => b[1] - a[1])
        .forEach(([type, count]) => {
            const name = HELPER_TYPE_LABELS[type] || type;
            rows.push(`${name},${count}`);
        });
    rows.push('');

    // Sessions by origin
    rows.push('SESSIONS BY ORIGIN');
    rows.push('Origin,Session Count');
    Object.entries(analytics.sessionsByOrigin)
        .sort((a, b) => b[1] - a[1])
        .forEach(([origin, count]) => {
            rows.push(`${origin},${count}`);
        });
    rows.push('');

    // Daily breakdown
    rows.push('DAILY BREAKDOWN');
    rows.push('Date,Sessions,Messages,Unique Users,Tokens,Flagged Messages,Avg Duration (s),Suggestions Shown,Suggestions Adopted');
    analytics.dailyStats.forEach(day => {
        rows.push([
            day.date,
            day.totalSessions,
            day.totalMessages,
            day.uniqueUsers,
            day.totalTokensUsed,
            day.flaggedMessageCount,
            day.avgSessionDurationSeconds.toFixed(1),
            day.totalSuggestionsShown,
            day.totalSuggestionsAdopted,
        ].join(','));
    });

    return rows.join('\n');
}

// ─── Moderation Queue Export ────────────────────────────────

export function exportModerationQueueToCSV(
    messages: FlaggedMessage[]
): string {
    const rows: string[] = [];
    rows.push('Omore Moderation Queue Export');
    rows.push(`Exported,${new Date().toISOString()}`);
    rows.push('');
    rows.push('Message ID,Explorer Name,Apple ID,Content,Flag Categories,Max Severity,Helper Type,Origin,Flagged At,Reviewed,Reviewed By,Review Action,Review Notes');

    messages.forEach(msg => {
        let timestamp: string;
        try {
            const date = msg.flaggedAt && (msg.flaggedAt as any).toDate
                ? (msg.flaggedAt as any).toDate()
                : new Date(msg.flaggedAt as any);
            timestamp = date.toISOString();
        } catch {
            timestamp = 'N/A';
        }

        const flagCategories = msg.flagCategories
            .map(cat => CATEGORY_LABELS[cat] || cat)
            .join('; ');

        rows.push([
            msg.id,
            escapeCsvField(msg.explorerName),
            msg.appleID,
            escapeCsvField(msg.messageContent),
            escapeCsvField(flagCategories),
            msg.maxSeverity,
            msg.helperType,
            msg.origin,
            timestamp,
            msg.reviewedAt ? 'Yes' : 'No',
            msg.reviewedBy || '',
            msg.reviewAction || '',
            escapeCsvField(msg.reviewNotes || ''),
        ].join(','));
    });

    return rows.join('\n');
}

// ─── User Abuse Summary Export ──────────────────────────────

export function exportUserAbuseSummaryToCSV(
    users: UserAbuseSummary[]
): string {
    const rows: string[] = [];
    rows.push('Omore User Abuse Summary Export');
    rows.push(`Exported,${new Date().toISOString()}`);
    rows.push('');
    rows.push('Apple ID,Explorer Name,Total Flags,Highest Severity,Top Categories,First Flagged,Last Flagged,Blocked,Blocked By,Block Reason');

    users.forEach(user => {
        const firstFlagged = formatTimestampForCSV(user.firstFlaggedAt);
        const lastFlagged = formatTimestampForCSV(user.lastFlaggedAt);

        const topCategories = Object.entries(user.flagsByCategory)
            .sort((a, b) => b[1] - a[1])
            .slice(0, 5)
            .map(([cat, count]) => `${CATEGORY_LABELS[cat] || cat} (${count})`)
            .join('; ');

        rows.push([
            user.appleID,
            escapeCsvField(user.explorerName),
            user.totalFlags,
            user.highestSeverity,
            escapeCsvField(topCategories),
            firstFlagged,
            lastFlagged,
            user.isBlocked ? 'Yes' : 'No',
            user.blockedBy || '',
            escapeCsvField(user.blockedReason || ''),
        ].join(','));
    });

    return rows.join('\n');
}

// ─── Session Details Export ─────────────────────────────────

export function exportSessionDetailsToCSV(
    detail: OmoreSessionDetail
): string {
    const rows: string[] = [];
    const { session, messages } = detail;

    rows.push('Omore Session Detail Export');
    rows.push(`Exported,${new Date().toISOString()}`);
    rows.push('');

    // Session header
    rows.push('SESSION INFO');
    rows.push('Field,Value');
    rows.push(`Session ID,${session.id}`);
    rows.push(`Explorer Name,${escapeCsvField(session.explorerName)}`);
    rows.push(`Apple ID,${session.appleID}`);
    rows.push(`Helper Type,${session.helperType}`);
    rows.push(`Origin,${session.origin}`);
    rows.push(`Started,${formatTimestampForCSV(session.startedAt)}`);
    rows.push(`Ended,${formatTimestampForCSV(session.endedAt)}`);
    rows.push(`Duration (seconds),${session.durationSeconds}`);
    rows.push(`Messages,${session.messageCount}`);
    rows.push(`Tokens Used,${session.totalTokensUsed}`);
    rows.push(`Suggestions Shown,${session.suggestionsShown}`);
    rows.push(`Suggestions Adopted,${session.suggestionsAdopted}`);
    rows.push(`Moderation Flag,${session.hasModerationFlag ? 'Yes' : 'No'}`);
    rows.push(`Device,${session.deviceType}`);
    rows.push(`App Version,${session.appVersion}`);
    rows.push('');

    // Conversation
    rows.push('CONVERSATION');
    rows.push('Sequence,Timestamp,Speaker,Content,Tokens,Is Suggestion,Suggestion Adopted,Moderation Flags');

    messages.forEach((msg, idx) => {
        const timestamp = formatTimestampForCSV(msg.timestamp);
        const flags = msg.moderationFlags
            ? msg.moderationFlags.map(f => CATEGORY_LABELS[f] || f).join('; ')
            : '';

        rows.push([
            idx + 1,
            timestamp,
            msg.role === 'user' ? session.explorerName : 'Omore',
            escapeCsvField(msg.content),
            msg.tokenCount,
            msg.isSuggestion ? 'Yes' : 'No',
            msg.suggestionAdopted ? 'Yes' : 'No',
            escapeCsvField(flags),
        ].join(','));
    });

    return rows.join('\n');
}

// ─── Cohort Analysis Export ─────────────────────────────────

export function exportCohortAnalysisToCSV(
    analysis: CohortAnalysis
): string {
    const rows: string[] = [];

    rows.push('Omore Cohort Analysis Export');
    rows.push(`Period,${analysis.period}`);
    rows.push(`Date Range,${analysis.startDate} to ${analysis.endDate}`);
    rows.push(`Total Users,${analysis.totalUsers}`);
    rows.push('');

    // Overall retention rates
    rows.push('OVERALL RETENTION RATES');
    rows.push('Period,Retention %');
    rows.push(`Week 1,${analysis.avgRetentionWeek1.toFixed(1)}%`);
    rows.push(`Week 2,${analysis.avgRetentionWeek2.toFixed(1)}%`);
    rows.push(`Week 4,${analysis.avgRetentionWeek4.toFixed(1)}%`);
    rows.push(`Month 2,${analysis.avgRetentionMonth2.toFixed(1)}%`);
    rows.push('');

    // Engagement distribution
    rows.push('ENGAGEMENT DISTRIBUTION');
    rows.push('Tier,User Count');
    Object.entries(analysis.engagementDistribution).forEach(([tier, count]) => {
        rows.push(`${tier},${count}`);
    });
    rows.push('');

    // Cohort details
    rows.push('COHORT DETAILS');
    rows.push('Cohort,Users,Avg Sessions,Week 1 %,Week 2 %,Week 3 %,Week 4 %,Power User %,Churned %');
    analysis.cohorts.forEach(cohort => {
        rows.push([
            escapeCsvField(cohort.cohortLabel),
            cohort.userCount,
            cohort.avgSessionsPerUser.toFixed(1),
            cohort.retention.week1.toFixed(1),
            cohort.retention.week2.toFixed(1),
            cohort.retention.week3.toFixed(1),
            cohort.retention.week4.toFixed(1),
            cohort.powerUserPercent.toFixed(1),
            cohort.churnedUserPercent.toFixed(1),
        ].join(','));
    });

    return rows.join('\n');
}

// ─── Utility ────────────────────────────────────────────────

function formatTimestampForCSV(ts: any): string {
    if (!ts) return 'N/A';
    try {
        const date = ts.toDate ? ts.toDate() : new Date(ts);
        return date.toISOString();
    } catch {
        return 'N/A';
    }
}
